import { OnBoardingPaywallScreenType, OnBoardingScreenOptions } from '@mindly/ui-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  flowCurrentScreenDataSelector,
  flowUseAdditionalPromotionalCodeDiscountSelector,
  onBoardingFlowSelector,
} from 'src/redux/onBoardingFlow/selector';

const cachePromocodeEndDate = new Map<'timer', number>();

export const useFlowPromocode = () => {
  const flowData = useSelector(onBoardingFlowSelector);
  const currentScreenData = useSelector(flowCurrentScreenDataSelector);
  const useAdditionalPromotionalCodeDiscount = useSelector(flowUseAdditionalPromotionalCodeDiscountSelector);

  const dataIsReady = useMemo(() => !!flowData, [flowData]);
  const hideTimer = useMemo(
    () => !dataIsReady || !!currentScreenData?.hideTimer,
    [dataIsReady, currentScreenData?.hideTimer]
  );

  const promocodeEndDate = useMemo(() => {
    const screenPromocodeDuration =
      currentScreenData?.type === OnBoardingScreenOptions.PAYWALL
        ? (currentScreenData as OnBoardingPaywallScreenType)?.promocodeDuration
        : 0;
    const flowPromocodeDuration = flowData?.promocode_duration_min ?? flowData?.duration_min ?? 0;

    const hasPromocodeDuration = !!screenPromocodeDuration || !!flowPromocodeDuration;

    if (!hasPromocodeDuration || hideTimer) return 0;

    if (cachePromocodeEndDate.get('timer')) return cachePromocodeEndDate.get('timer');
    const minutes = flowPromocodeDuration || screenPromocodeDuration;
    if (!minutes) return 0;
    const endDate = DateTime.now().plus({ minutes }).toMillis();
    cachePromocodeEndDate.set('timer', endDate);
    return endDate;
  }, [
    hideTimer,
    flowData?.duration_min,
    flowData?.promocode_duration_min,
    flowData?.promocode_percent,
    currentScreenData,
  ]);

  const promocodePercent = useMemo(() => {
    if (currentScreenData?.type === OnBoardingScreenOptions.PAYWALL) {
      return useAdditionalPromotionalCodeDiscount
        ? (currentScreenData as OnBoardingPaywallScreenType)?.additionalDiscount
        : (currentScreenData as OnBoardingPaywallScreenType)?.percentDiscount;
    }
    return flowData?.promocode_percent;
  }, [currentScreenData, flowData?.promocode_percent, useAdditionalPromotionalCodeDiscount]);

  return {
    promocodeEndDate,
    promocodePercent,
  };
};
